import logger from '../utils/logger';
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { buildApiUrl } from '../config/api';

const StudentPlanningContext = createContext(null);

export const StudentPlanningProvider = ({ children }) => {
  const { user, getAuthToken, refreshAuthToken } = useAuth();
  // AuthContext recreates getAuthToken/refreshAuthToken on every route change
  // (they close over react-router's `navigate`), so a fetcher that lists them
  // as a dependency is itself recreated on each navigation — and the prefetch
  // effect below, which depends on the fetchers, re-ran on every page: four
  // requests (190 KB of assignments among them) per tap on the bottom nav.
  // Read them through a ref instead: the fetchers keep one identity for the
  // whole session and the prefetch really runs once per student.
  const authRef = useRef({ getAuthToken, refreshAuthToken });
  authRef.current = { getAuthToken, refreshAuthToken };
  const [assignments, setAssignments] = useState([]);
  const [planningBlocks, setPlanningBlocks] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [planningBlocksLoading, setPlanningBlocksLoading] = useState(false);
  // True once the first request has settled (success or failure). The
  // *Loading flags start false before the prefetch fires, so they cannot tell
  // "not asked yet" from "done" — the boot splash waits on these instead.
  const [assignmentsLoaded, setAssignmentsLoaded] = useState(false);
  // Last request failed: an empty list then means "unknown", not "no session"
  // (the new-student welcome must not show on a network error).
  const [assignmentsFailed, setAssignmentsFailed] = useState(false);
  const [planningBlocksLoaded, setPlanningBlocksLoaded] = useState(false);
  const [lastFetchAt, setLastFetchAt] = useState(null);

  const fetchAssignments = useCallback(async () => {
    if (!user?.id) return;
    setAssignmentsLoading(true);
    try {
      let token = await authRef.current.getAuthToken();
      if (!token) { setAssignmentsFailed(true); return; }
      let response = await fetch(buildApiUrl('/api/assignments/student'), {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.status === 401 && authRef.current.refreshAuthToken) {
        const newToken = await authRef.current.refreshAuthToken();
        if (newToken) {
          response = await fetch(buildApiUrl('/api/assignments/student'), {
            headers: { 'Authorization': `Bearer ${newToken}`, 'Content-Type': 'application/json' }
          });
        }
      }
      if (!response.ok) throw new Error('Failed to fetch assignments');
      const data = await response.json();
      setAssignments(data.data || []);
      setAssignmentsFailed(false);
      setLastFetchAt(Date.now());
    } catch (err) {
      logger.error('Error fetching assignments:', err);
      setAssignmentsFailed(true);
    } finally {
      setAssignmentsLoading(false);
      setAssignmentsLoaded(true);
    }
  }, [user?.id]);

  const fetchPlanningBlocks = useCallback(async () => {
    if (!user?.id) return;
    setPlanningBlocksLoading(true);
    try {
      const token = await authRef.current.getAuthToken();
      if (!token) return;
      const response = await fetch(
        buildApiUrl(`/periodization/blocks/student/${user.id}?t=${Date.now()}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      setPlanningBlocks(data.success && Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      logger.error('Error fetching planning blocks:', err);
      setPlanningBlocks([]);
    } finally {
      setPlanningBlocksLoading(false);
      setPlanningBlocksLoaded(true);
    }
  }, [user?.id]);

  // RIS / classement (dashboard cards). Fetched HERE, once per session, rather
  // than by the dashboard on mount: the dashboard remounts on every navigation
  // back to it, and a per-mount fetch made the two cards pop in after the rest
  // of the page each time.
  const [rankings, setRankings] = useState(null);
  const [rankingsLoaded, setRankingsLoaded] = useState(false);
  // Feature payante : si le coach de l'élève est en plan gratuit, l'API renvoie
  // 403 — on ne tente pas l'appel (même garde que StudentRankings).
  // Le flag `features` peut manquer (réponse de /auth/login) : un 403
  // PLAN_RESTRICTED de l'API dit alors la même chose, et la page Classement
  // affiche l'écran « fonctionnalité payante » au lieu d'un classement vide.
  const [rankingsPlanRestricted, setRankingsPlanRestricted] = useState(false);
  const rankingsGated = user?.features?.rankings === false || rankingsPlanRestricted;

  const fetchRankings = useCallback(async () => {
    if (!user?.id || rankingsGated) return;
    try {
      const token = await authRef.current.getAuthToken();
      if (!token) return;
      const response = await fetch(buildApiUrl('/api/student/rankings'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRankings(data.data || null);
      } else if (response.status === 403) {
        const body = await response.json().catch(() => null);
        if (body?.code === 'PLAN_RESTRICTED') setRankingsPlanRestricted(true);
      }
    } catch {
      // Non-fatal and deliberately silent (no logger): the dashboard simply
      // renders without the cards, and e2e/student.spec.js asserts no console
      // errors.
    } finally {
      setRankingsLoaded(true);
    }
  }, [user?.id, rankingsGated]);

  // 30-day RIS history — the sparkline of the rankings screen's "Ton
  // classement" card. Fetched with the rankings so the card arrives whole
  // instead of the curve popping in a beat after the numbers.
  const [risHistory, setRisHistory] = useState([]);

  const fetchRisHistory = useCallback(async () => {
    if (!user?.id || rankingsGated) return;
    try {
      const token = await authRef.current.getAuthToken();
      if (!token) return;
      const response = await fetch(buildApiUrl('/api/student/ris-history?days=30'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) return;
      const data = await response.json();
      setRisHistory(Array.isArray(data.data?.points) ? data.data.points : []);
    } catch {
      // Non-fatal: the card simply renders without its sparkline.
    }
  }, [user?.id, rankingsGated]);

  // Student workout videos (the video library). Same reasoning as rankings:
  // the library remounts on every navigation to it, and a per-mount fetch put
  // a spinner in front of 40 cards on each visit (~115 KB, ~0.4 s locally,
  // more on Railway). Fetched HERE once per session; the library then shows
  // the cached list at once and refreshes it in the background on each visit
  // (signed URLs expire, new feedback lands).
  const [studentVideos, setStudentVideos] = useState([]);
  const [studentVideosLoaded, setStudentVideosLoaded] = useState(false);

  const fetchStudentVideos = useCallback(async () => {
    if (!user?.id) return;
    try {
      const token = await authRef.current.getAuthToken();
      if (!token) return;
      const response = await fetch(buildApiUrl('/api/workout-sessions/student-videos'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch student videos');
      const data = await response.json();
      if (data.success) setStudentVideos(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      logger.error('Error fetching student videos:', err);
    } finally {
      setStudentVideosLoaded(true);
    }
  }, [user?.id]);

  // Prefetch dès qu'un étudiant est connecté (sur toute page étudiant)
  useEffect(() => {
    if (user?.role !== 'student' || !user?.id) return;
    fetchAssignments();
    fetchPlanningBlocks();
    fetchRankings();
    fetchRisHistory();
    fetchStudentVideos();
  }, [user?.role, user?.id, fetchAssignments, fetchPlanningBlocks, fetchRankings, fetchRisHistory, fetchStudentVideos]);

  const refresh = useCallback(async () => {
    if (user?.role === 'student' && user?.id) {
      await Promise.all([fetchAssignments(), fetchPlanningBlocks(), fetchRankings()]);
    }
  }, [user?.role, user?.id, fetchAssignments, fetchPlanningBlocks, fetchRankings]);

  const value = {
    assignments,
    planningBlocks,
    assignmentsLoading,
    planningBlocksLoading,
    assignmentsLoaded,
    assignmentsFailed,
    planningBlocksLoaded,
    lastFetchAt,
    rankings,
    rankingsLoaded,
    rankingsPlanRestricted,
    risHistory,
    studentVideos,
    studentVideosLoaded,
    refreshStudentVideos: fetchStudentVideos,
    refreshAssignments: fetchAssignments,
    refreshPlanningBlocks: fetchPlanningBlocks,
    refreshRankings: fetchRankings,
    refreshRisHistory: fetchRisHistory,
    refresh
  };

  return (
    <StudentPlanningContext.Provider value={value}>
      {children}
    </StudentPlanningContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- context hook co-located with its provider (established convention; dev-only HMR hint)
export const useStudentPlanning = () => {
  const ctx = useContext(StudentPlanningContext);
  return ctx;
};
